import type { Marking, MarkingColor } from '~/utils/markings'
import { consola } from 'consola'
import { NOTE_TOOLTIP_OFFSET } from '~/constants/markings'
import {
  clampTooltipToViewport,
  closestMarkElement,
  isMarkingColor,
  isNoteIndicatorOpenEvent,
  formatMarkingTimestamp,
  NOTE_INDICATOR_OPEN_EVENT,
  recolorMarkingDom,
  setMarkingNoteIndicator,
} from '~/utils/markings'

export function useMarkingNoteTooltip() {
  const { allMarkings, updateMarking } = useMarkings()
  const { user } = useUserSession()
  watch(() => user.value?.userId, () => closeTooltip())

  const activeMarkingId = ref<string | null>(null)
  const fallbackMarking = ref<Marking | null>(null)
  const isEditing = ref(false)
  const isSavingNote = ref(false)
  const tooltipCoords = ref({ x: 0, y: 0 })
  const draftNote = ref('')
  const tooltipRef = ref<HTMLElement>()
  const textareaRef = ref<HTMLTextAreaElement>()

  const activeMarking = computed<Marking | null>(() => {
    const id = activeMarkingId.value

    if (!id) {
      return null
    }

    return allMarkings.value.find(marking => marking.id === id) ?? fallbackMarking.value
  })

  const hasSavedNote = computed(() => !!activeMarking.value?.note?.trim())

  const formatTimestamp = formatMarkingTimestamp

  function positionTooltipAt(mouseX: number, mouseY: number) {
    // position:fixed — viewport (client) coords apply directly, no scroll offset.
    tooltipCoords.value = {
      x: mouseX + NOTE_TOOLTIP_OFFSET.x,
      y: mouseY + NOTE_TOOLTIP_OFFSET.y,
    }
  }

  function reclampTooltip() {
    nextTick(() => {
      if (tooltipRef.value && activeMarkingId.value) {
        tooltipCoords.value = clampTooltipToViewport(tooltipRef.value, tooltipCoords.value)
      }
    })
  }

  function openFor(id: string, mouseX: number, mouseY: number, prefetched?: Marking) {
    const marking = prefetched ?? allMarkings.value.find(marking => marking.id === id)

    if (!marking) {
      return
    }

    activeMarkingId.value = id
    fallbackMarking.value = prefetched ?? null
    draftNote.value = marking.note ?? ''
    isEditing.value = !marking.note?.trim()
    positionTooltipAt(mouseX, mouseY)

    nextTick(() => {
      if (tooltipRef.value) {
        tooltipCoords.value = clampTooltipToViewport(tooltipRef.value, tooltipCoords.value)
      }

      if (isEditing.value) {
        textareaRef.value?.focus()
      }
    })
  }

  // The dialog changes height for several reasons — view ↔ edit, a saved note
  // arriving from the store a tick after the write resolves, window resize —
  // and measuring on any single one of those left it hanging off the bottom
  // edge until reopened. Observing the box itself covers them all.
  useResizeObserver(tooltipRef, reclampTooltip)

  function closeTooltip() {
    activeMarkingId.value = null
    fallbackMarking.value = null
    isEditing.value = false
    draftNote.value = ''
  }

  async function handleColorClick(color: MarkingColor) {
    const marking = activeMarking.value

    if (!marking || marking.color === color) {
      return
    }

    // The store applies the colour optimistically, so recolour the text with
    // it rather than a round trip later.
    recolorMarkingDom(marking.id, color)
    await updateMarking(marking.id, { color })

    // The store is the truth whatever the write did — rolled back, or dropped
    // because one on this marking was still in flight. No rehydration pass
    // would catch a colour left behind: the observers only watch `childList`.
    const settled = allMarkings.value.find(candidate => candidate.id === marking.id)

    if (settled && isMarkingColor(settled.color)) {
      recolorMarkingDom(marking.id, settled.color)
    }
  }

  async function saveNote(nextNote: string | null) {
    const marking = activeMarking.value

    if (!marking) {
      return
    }

    // activeMarking resolves to the fallback until the shared store catches
    // up — it must not show the pre-save note in that window.
    if (fallbackMarking.value?.id === marking.id) {
      fallbackMarking.value = { ...fallbackMarking.value, note: nextNote }
    }

    setMarkingNoteIndicator(marking.id, !!nextNote)

    if (!nextNote) {
      closeTooltip()
    }
    else if (draftNote.value.trim() === nextNote) {
      isEditing.value = false
    }

    // Clicking outside is blocked for the round trip: closing the tooltip here
    // would take the draft with it, and a refused write has nowhere to put it
    // back — the toast promises it is kept.
    isSavingNote.value = true
    const saved = await updateMarking(marking.id, { note: nextNote })
    isSavingNote.value = false

    if (saved) {
      return
    }

    // The write was refused, either rolled back or dropped because one on this
    // marking was still in flight: put the draft and the old indicator back.
    if (import.meta.dev) {
      consola.warn(`[markings] note save refused for id=${marking.id}`)
    }

    if (activeMarkingId.value === marking.id) {
      draftNote.value = nextNote ?? ''
      isEditing.value = true
    }

    setMarkingNoteIndicator(marking.id, !!marking.note?.trim())
  }

  function handleSave() {
    const trimmed = draftNote.value.trim()
    saveNote(trimmed.length > 0 ? trimmed : null)
  }

  function handleTextareaKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      handleSave()
    }
  }

  function handleEditStart(event?: MouseEvent) {
    event?.stopPropagation()

    if (!activeMarking.value) {
      return
    }

    isEditing.value = true
    draftNote.value = activeMarking.value.note?.trim() ?? ''
    nextTick(() => textareaRef.value?.focus())
  }

  function handleDelete() {
    const marking = activeMarking.value

    if (!marking || !hasSavedNote.value) {
      return
    }

    saveNote(null)
  }

  useEventListener(document, 'click', (event: MouseEvent) => {
    const targetEl = event.target instanceof Element ? event.target : null

    if (!targetEl) {
      return
    }

    if (tooltipRef.value?.contains(targetEl)) {
      return
    }

    // Mid-edit or mid-save, stray clicks must not close the tooltip and lose
    // the draft.
    if (activeMarkingId.value && (isEditing.value || isSavingNote.value)) {
      return
    }

    // Clicking marked text is owned by the selection-tooltip flow: it opens
    // this window for noted markings (via NOTE_INDICATOR_OPEN_EVENT) and the
    // color tooltip otherwise — don't race it by closing here.
    const mark = closestMarkElement(targetEl)

    if (mark) {
      const id = mark.dataset.markingId

      if (id && allMarkings.value.find(marking => marking.id === id)?.note?.trim()) {
        return
      }
    }

    if (activeMarkingId.value) {
      closeTooltip()
    }
  })

  useEventListener(document, NOTE_INDICATOR_OPEN_EVENT, (event: Event) => {
    if (!isNoteIndicatorOpenEvent(event)) {
      return
    }

    const { id, clientX, clientY, marking } = event.detail
    openFor(id, clientX, clientY, marking)
  })

  useEventListener(document, 'keydown', (event: KeyboardEvent) => {
    if (!activeMarkingId.value) {
      return
    }

    if (event.key === 'Escape') {
      closeTooltip()
    }
  })

  // Scrolling deliberately does NOT dismiss the note — it stays until the
  // user closes it (X, Escape, or clicking outside).

  return {
    activeMarking,
    isEditing,
    draftNote,
    tooltipCoords,
    tooltipRef,
    textareaRef,
    hasSavedNote,
    formatTimestamp,
    closeTooltip,
    handleColorClick,
    handleSave,
    handleTextareaKeydown,
    handleEditStart,
    handleDelete,
  }
}
